import { useEffect } from 'react';

import { isEmbedded } from '@/shared/embedded';

import { isAuthenticated } from './session';

/* A page restored from the back/forward cache must not come back signed in.
 *
 * **The problem this closes, and why the route guard does not already close it.** `RequireAuth`
 * re-evaluates `isAuthenticated()` on every render, so an in-app Back press redirects to `/login`
 * the moment the router re-renders the tree. What it cannot see is a *document* restore: when a
 * browser serves a page from the back/forward cache it restores the whole JavaScript heap — the
 * React tree, the query cache, the rendered DOM — and runs no render at all. So pressing Back onto
 * an app screen after signing out shows that screen, fully populated with the previous reader's
 * data, and nothing in React is invoked to notice.
 *
 * The data on it is stale rather than live — every request from that point answers 401 and the
 * screen degrades — which is precisely what makes it dangerous: the *first paint* is a working
 * dashboard belonging to somebody who has signed out, on a shared machine, and it looks correct.
 *
 * **`pageshow` with `event.persisted` is the one signal a bfcache restore raises.** It fires on
 * every load, and `persisted` is true only for a cache restore, so this is the narrow test rather
 * than a reload on every navigation. `window.location.reload()` rather than a router navigation: the
 * heap is what is stale, and only a fresh document discards it — a `navigate()` would leave the
 * restored query cache in place behind whatever rendered next.
 *
 * **Guarded on `isAuthenticated()` being false**, so a reader who merely navigated away and back
 * while still signed in is not reloaded. Without that test this would throw away a perfectly good
 * page and its scroll position on every Back press, which is how a guard like this comes to be
 * deleted.
 *
 * Mounted once, at the root. It listens on `window` rather than being attached per route, because
 * the event fires against the document and a per-route listener would only be attached for whichever
 * route happened to be mounted when the page was cached.
 */
export function useSessionRestoreGuard(): void {
  useEffect(() => {
    /* **Not inside the Suite shell.** The page there is the shell's, and so is the session: a reload
     * would throw away every product on the page to fix a state only the shell can judge, and a
     * product never reloads the window. The shell runs its own restore check. */
    if (isEmbedded()) return undefined;
    const onPageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted || isAuthenticated()) return;
      window.location.reload();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);
}
